import React, { useState, useEffect, useCallback } from "react";
import {
  MdOutlineFlag, MdOutlineGroup, MdOutlinePayments,
  MdOutlineCheckCircle, MdOutlineDelete, MdOutlineBlock,
  MdOutlineRefresh, MdOutlineLogout, MdOutlineSearch,
  MdOutlinePending, MdOutlineBuild,
  MdOutlineVisibility, MdOutlineArticle, MdOutlinePerson,
  MdOutlineForum, MdOutlineCelebration, MdOutlineClose,
  MdOutlineNotifications, MdOutlineSend, MdOutlineBarChart,
  MdOutlineTrendingUp, MdOutlineStar, MdOutlineImage,
  MdOutlineCalendarToday, MdOutlineSpeed, MdOutlineDevices,
  MdOutlineDashboard, MdOutlinePersonAdd, MdOutlineEditNote,
  MdOutlineWarningAmber, MdOutlineMenu,
} from "react-icons/md";

const API = "https://api.ninoa.space";
const LOGO = "/img/N_logo_pink_background.png";

/* ───── helpers ───── */
const statusColors = {
  pending: { bg: "#fff3e0", color: "#e65100" },
  reviewed: { bg: "#e3f2fd", color: "#1565c0" },
  actioned: { bg: "#e8f5e9", color: "#2e7d32" },
  dismissed: { bg: "#f3e5f5", color: "#7b1fa2" },
};
const pill = (bg, color, label) => (
  <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: bg, color }}>{label}</span>
);
function timeAgo(d) {
  if (!d) return "";
  const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/* ───── LOGIN ───── */
function LoginScreen({ onLogin }) {
  const [key, setKey] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const r = await fetch(`${API}/admin/stats`, { headers: { "x-admin-key": key } });
      if (r.ok) { localStorage.setItem("ninoa_admin_key", key); onLogin(key); }
      else setError("Invalid admin key");
    } catch { setError("Cannot reach server"); } finally { setLoading(false); }
  };

  return (
    <div style={s.loginWrapper}>
      <div style={s.loginCard}>
        <img src={LOGO} alt="Ninoa" style={{ width: 72, height: 72, borderRadius: 20, margin: "0 auto 20px", display: "block" }} />
        <h1 style={{ fontSize: 24, fontWeight: 800, color: "#40284a", margin: 0 }}>Ninoa Admin</h1>
        <p style={{ fontSize: 14, color: "#6D5B80", margin: "8px 0 28px" }}>Your command center</p>
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <input type="password" placeholder="Admin key" value={key} onChange={e => setKey(e.target.value)} style={s.input} autoFocus />
          {error && <p style={{ color: "#e53935", fontSize: 13, margin: 0 }}>{error}</p>}
          <button type="submit" style={s.primaryBtn} disabled={loading || !key}>{loading ? "Verifying..." : "Enter Dashboard"}</button>
        </form>
      </div>
    </div>
  );
}

/* ───── SHARED COMPONENTS ───── */
function StatCard({ label, value, icon, color, subtitle }) {
  return (
    <div style={{ ...s.card, display: "flex", alignItems: "center", gap: 16, padding: "20px 24px" }}>
      <div style={{ width: 48, height: 48, borderRadius: 14, background: color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {React.cloneElement(icon, { color: "#fff" })}
      </div>
      <div>
        <div style={{ fontSize: 28, fontWeight: 800, color: "#40284a", lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 13, color: "#6D5B80", fontWeight: 500 }}>{label}</div>
        {subtitle && <div style={{ fontSize: 11, color: color, fontWeight: 600, marginTop: 2 }}>{subtitle}</div>}
      </div>
    </div>
  );
}

function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-block" }} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && <span style={s.tooltip}>{text}</span>}
    </span>
  );
}

function MiniBar({ data, maxVal, color = "#7e3fc3", height = 80 }) {
  const max = maxVal || Math.max(...data.map(d => parseInt(d.count) || 0), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height }}>
      {data.map((d, i) => (
        <Tooltip key={i} text={`${d.date}: ${d.count}`}>
          <div style={{
            flex: 1, minWidth: 8, maxWidth: 16,
            height: Math.max(3, (parseInt(d.count) / max) * (height - 4)),
            background: `linear-gradient(180deg, ${color}, ${color}88)`,
            borderRadius: 4, cursor: "pointer", transition: "height 0.3s",
          }} />
        </Tooltip>
      ))}
    </div>
  );
}

function SectionTitle({ icon, title, subtitle }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: "#fce6ee", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {icon}
      </div>
      <div>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: "#40284a", margin: 0 }}>{title}</h3>
        {subtitle && <p style={{ fontSize: 12, color: "#6D5B80", margin: 0 }}>{subtitle}</p>}
      </div>
    </div>
  );
}

/* ═══════ OVERVIEW PAGE (NEW — THE COCKPIT) ═══════ */
function OverviewPage({ hdr, stats, analytics }) {
  const [analyticsData, setAnalyticsData] = useState(analytics);
  const [loading, setLoading] = useState(!analytics);

  useEffect(() => {
    if (!analyticsData) {
      (async () => {
        try {
          const r = await fetch(`${API}/admin/analytics`, { headers: hdr });
          if (r.ok) setAnalyticsData(await r.json());
        } catch {} finally { setLoading(false); }
      })();
    }
  }, [hdr, analyticsData]);

  return (
    <>
      {/* Pulse: Key numbers */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <img src={LOGO} alt="Ninoa" style={{ width: 32, height: 32, borderRadius: 8 }} />
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: "#40284a", margin: 0 }}>Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"}</h2>
            <p style={{ fontSize: 13, color: "#6D5B80", margin: 0 }}>Here's how Ninoa is doing today</p>
          </div>
        </div>

        <div className="admin-grid-4" style={s.grid4}>
          <StatCard
            label="Total Users"
            value={stats?.totalUsers || 0}
            icon={<MdOutlineGroup size={24} color="#7e3fc3" />}
            color="#7e3fc3"
          />
          <StatCard
            label="Active (7 days)"
            value={analyticsData?.activeUsers7d || 0}
            icon={<MdOutlineSpeed size={24} color="#2e7d32" />}
            color="#2e7d32"
            subtitle={analyticsData?.totalUsers ? `${Math.round((analyticsData.activeUsers7d / analyticsData.totalUsers) * 100)}% of users` : null}
          />
          <StatCard
            label="Pending Reports"
            value={stats?.pendingReports || 0}
            icon={<MdOutlineWarningAmber size={24} color={stats?.pendingReports > 0 ? "#e65100" : "#8b7a9e"} />}
            color={stats?.pendingReports > 0 ? "#e65100" : "#e0d6ea"}
            subtitle={stats?.pendingReports > 0 ? "Needs attention" : "All clear"}
          />
          <StatCard
            label="Total Posts"
            value={stats?.totalPosts || 0}
            icon={<MdOutlineForum size={24} color="#1565c0" />}
            color="#1565c0"
          />
        </div>
      </div>

      {/* Activity Charts */}
      {analyticsData && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 }}>
          <div style={s.card}>
            <SectionTitle icon={<MdOutlineEditNote size={20} color="#7e3fc3" />} title="Health Logs" subtitle="Last 30 days" />
            {analyticsData.dailyLogs?.length > 0
              ? <MiniBar data={analyticsData.dailyLogs} color="#7e3fc3" height={100} />
              : <p style={{ color: "#6D5B80", fontSize: 13 }}>No data yet</p>
            }
          </div>
          <div style={s.card}>
            <SectionTitle icon={<MdOutlineForum size={20} color="#1565c0" />} title="Community Posts" subtitle="Last 30 days" />
            {analyticsData.dailyPosts?.length > 0
              ? <MiniBar data={analyticsData.dailyPosts} color="#1565c0" height={100} />
              : <p style={{ color: "#6D5B80", fontSize: 13 }}>No data yet</p>
            }
          </div>
        </div>
      )}

      {/* Bottom row: engagement + top users */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Engagement snapshot */}
        <div style={s.card}>
          <SectionTitle icon={<MdOutlineTrendingUp size={20} color="#2e7d32" />} title="Engagement Snapshot" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[
              { label: "Posts (7d)", value: analyticsData?.postsLast7d || 0, color: "#7e3fc3" },
              { label: "Posts (30d)", value: analyticsData?.postsLast30d || 0, color: "#1565c0" },
              { label: "Active (30d)", value: analyticsData?.activeUsers30d || 0, color: "#2e7d32" },
              { label: "Total Logs", value: analyticsData?.totalLogs || 0, color: "#e65100" },
            ].map((item, i) => (
              <div key={i} style={{ padding: 16, background: "#fce6ee", borderRadius: 14, textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: "#40284a" }}>{item.value}</div>
                <div style={{ fontSize: 12, color: "#6D5B80", marginTop: 2 }}>{item.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Top users */}
        <div style={s.card}>
          <SectionTitle icon={<MdOutlineStar size={20} color="#e65100" />} title="Top Active Users" subtitle="Last 30 days" />
          {analyticsData?.topUsers?.length > 0 ? analyticsData.topUsers.slice(0, 5).map((u, i) => (
            <div key={u.userId} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "10px 0",
              borderBottom: i < Math.min(analyticsData.topUsers.length, 5) - 1 ? "1px solid #e8c8d8" : "none",
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: ["#7e3fc3", "#1565c0", "#2e7d32", "#e65100", "#c62828"][i],
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontSize: 12, fontWeight: 800,
              }}>
                {i + 1}
              </div>
              <span style={{ fontSize: 14, color: "#40284a", flex: 1, fontWeight: 600 }}>{u.displayName}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#7e3fc3" }}>{u.logCount} logs</span>
            </div>
          )) : <p style={{ color: "#6D5B80", fontSize: 13 }}>No activity yet</p>}
        </div>
      </div>
    </>
  );
}

/* ═══════ REPORTS PAGE ═══════ */
function ReportsPage({ hdr, stats, onRefresh }) {
  const [reports, setReports] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetch_ = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/reports`, { headers: hdr });
      if (r.ok) setReports(await r.json());
    } catch {} finally { setLoading(false); }
  }, [hdr]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const act = async (id, action) => {
    if (action === "delete") {
      const rp = reports.find(r => r.id === id);
      if (!window.confirm("Delete this post permanently?")) return;
      if (rp?.postId) await fetch(`${API}/admin/post/${rp.postId}`, { method: "DELETE", headers: hdr });
    } else {
      await fetch(`${API}/admin/reports/${id}`, { method: "PUT", headers: { ...hdr, "Content-Type": "application/json" }, body: JSON.stringify({ status: action }) });
    }
    fetch_(); onRefresh();
  };

  const filtered = filter === "all" ? reports : reports.filter(r => r.status === filter);

  return (
    <>
      <div className="admin-grid-4" style={s.grid4}>
        <StatCard label="Total Reports" value={stats?.totalReports || 0} icon={<MdOutlineFlag size={24} color="#7e3fc3" />} color="#7e3fc3" />
        <StatCard label="Pending" value={stats?.pendingReports || 0} icon={<MdOutlinePending size={24} color="#e65100" />} color="#e65100" />
        <StatCard label="Reviewed" value={stats?.reviewedReports || 0} icon={<MdOutlineVisibility size={24} color="#1565c0" />} color="#1565c0" />
        <StatCard label="Actioned" value={stats?.actionedReports || 0} icon={<MdOutlineBuild size={24} color="#2e7d32" />} color="#2e7d32" />
      </div>
      <div style={s.filterRow}>
        {["all", "pending", "reviewed", "actioned", "dismissed"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ ...s.filterBtn, ...(filter === f ? s.filterActive : {}) }}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {loading ? <div style={s.empty}><p>Loading...</p></div> :
        filtered.length === 0 ? (
          <div style={s.empty}>
            <MdOutlineCelebration size={48} color="#7e3fc3" />
            <h3 style={{ color: "#6d5b80", marginTop: 12 }}>No reports</h3>
            <p style={{ color: "#6D5B80", fontSize: 14 }}>{filter === "pending" ? "All caught up!" : "No reports here."}</p>
          </div>
        ) : filtered.map(rp => {
          const sc = statusColors[rp.status] || statusColors.pending;
          return (
            <div key={rp.id} style={s.card}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <strong style={{ fontSize: 15, color: "#40284a", textTransform: "capitalize" }}>{rp.reason}</strong>
                {pill(sc.bg, sc.color, rp.status)}
                <span style={{ fontSize: 12, color: "#6D5B80", marginLeft: "auto" }}>{timeAgo(rp.createdAt)}</span>
              </div>
              <p style={{ fontSize: 14, color: "#33283a", margin: "4px 0", lineHeight: 1.5 }}>
                Post: <em>"{(rp.post?.content || "N/A").substring(0, 150)}"</em>
              </p>
              <p style={{ fontSize: 12, color: "#6D5B80", margin: "0 0 12px" }}>
                Reported by: {rp.reporter?.displayName || "Unknown"}{rp.details && <> &middot; {rp.details}</>}
              </p>
              {rp.status === "pending" && (
                <div style={{ display: "flex", gap: 8, borderTop: "1px solid #e8c8d8", paddingTop: 12 }}>
                  <Tooltip text="Acknowledge the report. Post stays visible but is marked as seen.">
                    <button style={{ ...s.actBtn, background: "#e3f2fd", color: "#1565c0" }} onClick={() => act(rp.id, "reviewed")}><MdOutlineCheckCircle size={16} /> Reviewed</button>
                  </Tooltip>
                  <Tooltip text="Permanently delete the post for all users.">
                    <button style={{ ...s.actBtn, background: "#ffebee", color: "#c62828" }} onClick={() => act(rp.id, "delete")}><MdOutlineDelete size={16} /> Delete Post</button>
                  </Tooltip>
                  <Tooltip text="Dismiss — the report is invalid, no action needed.">
                    <button style={{ ...s.actBtn, background: "#f3e5f5", color: "#7b1fa2" }} onClick={() => act(rp.id, "dismissed")}><MdOutlineClose size={16} /> Dismiss</button>
                  </Tooltip>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

/* ═══════ USER DETAIL MODAL ═══════ */
function UserDetailModal({ userId, hdr, onClose }) {
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLoading(true);
      try {
        const [uRes, lRes, usersRes] = await Promise.allSettled([
          fetch(`${API}/admin/users/${userId}`, { headers: hdr }),
          fetch(`${API}/admin/users/${userId}/logs`, { headers: hdr }),
          fetch(`${API}/admin/users?search=`, { headers: hdr }),
        ]);
        if (uRes.status === "fulfilled" && uRes.value.ok) {
          const userData = await uRes.value.json();
          // Merge streak/logDates from the users list if available
          if (usersRes.status === "fulfilled" && usersRes.value.ok) {
            const allUsers = await usersRes.value.json();
            const enriched = allUsers.find(u => String(u.id) === String(userId));
            if (enriched) {
              userData.currentStreak = enriched.currentStreak;
              userData.bestStreak = enriched.bestStreak;
              userData.logDates = enriched.logDates;
            }
          }
          setUser(userData);
        }
        if (lRes.status === "fulfilled" && lRes.value.ok) setLogs(await lRes.value.json());
      } catch {} finally { setLoading(false); }
    })();
  }, [userId, hdr]);

  const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const subLabel = (u) => {
    if (!u.subscriptionActive) return "Free";
    if (u.subscriptionPlan === "admin_grant") return "Admin Grant";
    return "Pro";
  };

  // Compute age from a "YYYY-MM-DD" birthdate string. "—" if missing/invalid.
  const computeAge = (birthdate) => {
    if (!birthdate) return "—";
    const d = new Date(birthdate);
    if (Number.isNaN(d.getTime())) return "—";
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return `${age}y`;
  };

  const yesNo = (v) => (v ? "✓" : "✗");
  const platformLabel = (p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : "—");
  const providerLabel = (p) => {
    if (p === "google") return "Google";
    if (p === "apple") return "Apple";
    if (p === "email") return "Email/password";
    return "—";
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(43,25,46,0.5)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "#fce6ee", borderRadius: 20, width: 500, maxWidth: "92vw",
        maxHeight: "85vh", overflow: "auto", position: "relative", padding: "32px 28px",
        boxShadow: "0 20px 60px rgba(43,25,46,0.25)",
      }}>
        {/* Close button */}
        <button onClick={onClose} style={{
          position: "absolute", top: 16, right: 16, background: "none", border: "none",
          cursor: "pointer", color: "#6D5B80", padding: 4,
        }}>
          <MdOutlineClose size={22} />
        </button>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ width: 32, height: 32, border: "3px solid #e8d5e0", borderTopColor: "#7e3fc3", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
            <p style={{ color: "#6D5B80", fontSize: 14 }}>Loading user details...</p>
          </div>
        ) : !user ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <p style={{ color: "#6D5B80", fontSize: 14 }}>Could not load user details.</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: "#40284a", margin: "0 0 4px" }}>{user.displayName || "Unknown"}</h2>
              <p style={{ fontSize: 13, color: "#6D5B80", margin: 0 }}>{user.email}</p>
            </div>

            {/* User Info Card */}
            <div style={{ background: "#fff5f8", borderRadius: 16, padding: "20px 22px", marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: "#40284a", margin: "0 0 14px" }}>User Info</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 20px" }}>
                {[
                  { label: "Condition", value: user.condition || "—" },
                  { label: "Diagnosed Year", value: user.diagnosedYear || "—" },
                  { label: "Gender", value: user.gender || "—" },
                  { label: "Age", value: computeAge(user.birthdate) },
                  { label: "Birthdate", value: user.birthdate || "—" },
                  { label: "Country", value: user.country || "—" },
                  { label: "City", value: user.city || "—" },
                  { label: "Postal Code", value: user.postalCode || "—" },
                  { label: "Language", value: user.locale || user.language || "—" },
                  { label: "Platform", value: platformLabel(user.platform) },
                  { label: "Sign-in Provider", value: providerLabel(user.provider) },
                  { label: "Signed Up", value: fmtDate(user.createdAt) },
                  { label: "Subscription", value: subLabel(user) },
                  { label: "Plan", value: user.subscriptionPlan || "—" },
                  { label: "Current Streak", value: user.currentStreak ? `${user.currentStreak} days` : "—" },
                  { label: "Best Streak", value: user.bestStreak ? `${user.bestStreak} days` : "—" },
                  { label: "Last Log", value: fmtDate(user.lastLogDate || user.lastActivity) },
                  { label: "Terms Accepted", value: yesNo(user.termsAccepted) },
                  { label: "Privacy Accepted", value: yesNo(user.privacyAccepted) },
                  { label: "Research Consent", value: yesNo(user.dataResearchConsent) },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6D5B80" }}>{item.label}</span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#33283a", textTransform: item.label === "Condition" ? "capitalize" : "none" }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Log Dates Card */}
            <div style={{ background: "#fff5f8", borderRadius: 16, padding: "20px 22px" }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: "#40284a", margin: "0 0 14px" }}>
                Log Dates {user.logDates && <span style={{ fontWeight: 500, color: "#6D5B80" }}>({user.logDates.length} unique days)</span>}
              </h3>
              {!user.logDates || user.logDates.length === 0 ? (
                <p style={{ color: "#6D5B80", fontSize: 13, margin: 0 }}>No logs recorded yet.</p>
              ) : (
                <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {user.logDates.map((date, i) => (
                    <div key={i} style={{
                      padding: "6px 12px", background: "#fce6ee", borderRadius: 10,
                      fontSize: 12, color: "#33283a", fontWeight: 600,
                    }}>
                      {new Date(date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════ USERS PAGE ═══════ */
function UsersPage({ hdr, stats }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [premiumLoading, setPremiumLoading] = useState(null);
  const [premiumMsg, setPremiumMsg] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const [conditionFilter, setConditionFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  // Hide TestersCommunity tester accounts by default — they have the pattern
  // firstname.lastname.NNNNN@gmail.com (Google's 12-tester closed-testing
  // requirement to publish to Production). Toggle off to see them.
  const [hideTesters, setHideTesters] = useState(true);
  const isTester = (email) => /\.\d{5}@gmail\.com$/i.test(email || "");

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const params = [];
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      if (conditionFilter && conditionFilter !== "all") params.push(`condition=${conditionFilter}`);
      if (platformFilter && platformFilter !== "all") params.push(`platform=${platformFilter}`);
      const url = `${API}/admin/users${params.length ? '?' + params.join('&') : ''}`;
      const r = await fetch(url, { headers: hdr });
      if (r.ok) setUsers(await r.json());
    } catch {} finally { setLoading(false); }
  }, [hdr, search, conditionFilter, platformFilter]);

  useEffect(() => { const t = setTimeout(fetch_, search ? 300 : 0); return () => clearTimeout(t); }, [fetch_, search]);

  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}" permanently? Removes from Cognito and database.`)) return;
    await fetch(`${API}/admin/users/${id}`, { method: "DELETE", headers: hdr });
    fetch_();
  };

  const togglePremium = async (user) => {
    const granting = !user.subscriptionActive;
    const action = granting ? "Grant Premium to" : "Revoke Premium from";
    if (!window.confirm(`${action} "${user.displayName}"?`)) return;
    setPremiumLoading(user.id);
    setPremiumMsg(null);
    try {
      const body = granting
        ? { subscriptionActive: true, subscriptionPlan: "admin_grant", subscriptionExpiresAt: "2027-12-31T00:00:00.000Z" }
        : { subscriptionActive: false, subscriptionPlan: null, subscriptionExpiresAt: null };
      const r = await fetch(`${API}/admin/users/${user.id}`, {
        method: "PUT",
        headers: { ...hdr, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        setPremiumMsg({ id: user.id, type: "success", text: granting ? "Premium granted!" : "Premium revoked." });
        fetch_();
      } else {
        setPremiumMsg({ id: user.id, type: "error", text: "Failed to update." });
      }
    } catch {
      setPremiumMsg({ id: user.id, type: "error", text: "Network error." });
    } finally {
      setPremiumLoading(null);
      setTimeout(() => setPremiumMsg(null), 3000);
    }
  };

  // Segment users — optionally hiding TestersCommunity accounts so launch
  // metrics reflect real users (the ~12 testers needed for Google's Production
  // requirement would otherwise inflate every count).
  const now = Date.now();
  const testerCount = users.filter(u => isTester(u.email)).length;
  const usersForSegment = hideTesters ? users.filter(u => !isTester(u.email)) : users;
  const segments = {
    all: usersForSegment,
    new: usersForSegment.filter(u => u.createdAt && (now - new Date(u.createdAt).getTime()) < 7 * 86400000),
    active: usersForSegment.filter(u => u.lastActivity && (now - new Date(u.lastActivity).getTime()) < 7 * 86400000),
    atRisk: usersForSegment.filter(u => {
      if (!u.lastActivity) return false;
      const diff = now - new Date(u.lastActivity).getTime();
      return diff >= 7 * 86400000 && diff < 30 * 86400000;
    }),
    churned: usersForSegment.filter(u => !u.lastActivity || (now - new Date(u.lastActivity).getTime()) >= 30 * 86400000),
  };
  const [segment, setSegment] = useState("all");
  const displayUsers = segments[segment] || users;

  return (
    <>
      <div className="admin-grid-4" style={s.grid4}>
        <StatCard label="Total Users" value={stats?.totalUsers || 0} icon={<MdOutlineGroup size={24} color="#7e3fc3" />} color="#7e3fc3" />
        <StatCard label="Active (7d)" value={segments.active.length} icon={<MdOutlineSpeed size={24} color="#2e7d32" />} color="#2e7d32" />
        <StatCard label="At Risk" value={segments.atRisk.length} icon={<MdOutlineWarningAmber size={24} color="#e65100" />} color="#e65100" subtitle="No activity 7-30d" />
        <StatCard label="Churned" value={segments.churned.length} icon={<MdOutlineBlock size={24} color="#c62828" />} color="#c62828" subtitle="No activity 30d+" />
      </div>

      {/* Search + segment filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 240 }}>
          <MdOutlineSearch size={20} color="#8b7a9e" style={{ position: "absolute", left: 16, top: 14 }} />
          <input type="text" placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...s.input, paddingLeft: 44, width: "100%", textAlign: "left" }} />
        </div>
        <select value={conditionFilter} onChange={e => setConditionFilter(e.target.value)}
          style={{ ...s.input, width: 180, textAlign: "left", padding: "10px 14px", fontSize: 13 }}>
          <option value="all">All Conditions</option>
          <option value="psoriasis">Psoriasis</option>
          <option value="eczema">Eczema</option>
          <option value="rosacea">Rosacea</option>
          <option value="acne">Acne</option>
          <option value="urticaria">Urticaria</option>
          <option value="hidradenitis">Hidradenitis</option>
          <option value="vitiligo">Vitiligo</option>
          <option value="alopecia">Alopecia</option>
          <option value="perioralDermatitis">Perioral Dermatitis</option>
          <option value="seborrheicDermatitis">Seborrheic Dermatitis</option>
        </select>
        <select value={platformFilter} onChange={e => setPlatformFilter(e.target.value)}
          style={{ ...s.input, width: 140, textAlign: "left", padding: "10px 14px", fontSize: 13 }}>
          <option value="all">All Platforms</option>
          <option value="ios">iOS</option>
          <option value="android">Android</option>
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#6d5b80", cursor: "pointer", padding: "8px 12px", background: hideTesters ? "#fce6ee" : "transparent", borderRadius: 8, border: "1px solid #e8c8d8" }}>
          <input type="checkbox" checked={hideTesters} onChange={e => setHideTesters(e.target.checked)} style={{ cursor: "pointer" }} />
          Hide {testerCount} TestersCommunity
        </label>
        <div style={{ display: "flex", gap: 6 }}>
          {[
            { key: "all", label: "All" },
            { key: "new", label: "New" },
            { key: "active", label: "Active" },
            { key: "atRisk", label: "At Risk" },
            { key: "churned", label: "Churned" },
          ].map(seg => (
            <button key={seg.key} onClick={() => setSegment(seg.key)}
              style={{ ...s.filterBtn, fontSize: 12, padding: "6px 14px", ...(segment === seg.key ? s.filterActive : {}) }}>
              {seg.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {loading ? <div style={s.empty}><p>Loading...</p></div> :
        displayUsers.length === 0 ? (
          <div style={s.empty}>
            <MdOutlinePerson size={48} color="#c4b5d0" />
            <h3 style={{ color: "#6d5b80", marginTop: 12 }}>No users found</h3>
          </div>
        ) : (
          <div className="admin-table-wrap">
            <div className="admin-table-row" style={{
              display: "grid", gridTemplateColumns: "40px 120px 1fr 100px 70px 50px 55px 55px 240px",
              gap: 12, padding: "10px 24px", background: "#fce6ee", fontWeight: 700, fontSize: 12,
              color: "#6d5b80", borderRadius: 12,
            }}>
              <span>ID</span><span>Name</span><span>Email</span><span>Condition</span><span>Platform</span><span>Logs</span><span>Streak</span><span>Sub</span><span>Actions</span>
            </div>
            {displayUsers.map(u => (
              <div key={u.id} className="admin-table-row" onClick={() => setSelectedUserId(u.id)} style={{
                ...s.card, display: "grid", gridTemplateColumns: "40px 120px 1fr 100px 70px 50px 55px 55px 240px",
                gap: 12, alignItems: "center", padding: "14px 24px", borderRadius: 14, marginBottom: 8,
                cursor: "pointer", transition: "box-shadow 0.2s",
              }} onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 20px rgba(61,43,66,0.12)"}
                 onMouseLeave={e => e.currentTarget.style.boxShadow = "0 2px 12px rgba(61,43,66,0.06)"}>
                <span style={{ fontSize: 12, color: "#6D5B80" }}>#{u.id}</span>
                <div>
                  <strong style={{ fontSize: 14, color: "#40284a" }}>{u.displayName}</strong>
                  {isTester(u.email) && (
                    <span style={{ display: "inline-block", marginLeft: 6, padding: "1px 6px", background: "#fff3e0", color: "#e65100", borderRadius: 6, fontSize: 9, fontWeight: 700, letterSpacing: 0.5, verticalAlign: "middle" }}>TESTER</span>
                  )}
                  {u.bio && <p style={{ fontSize: 11, color: "#6D5B80", margin: 0 }}>{u.bio.substring(0, 40)}</p>}
                </div>
                <span style={{ fontSize: 12, color: "#6d5b80", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</span>
                <span style={{ fontSize: 11, color: "#40284a", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.condition || "—"}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: u.platform === "ios" ? "#0a7ea4" : u.platform === "android" ? "#2e7d32" : "#6D5B80" }}>{u.platform === "ios" ? "iOS" : u.platform === "android" ? "Android" : "—"}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#33283a" }}>{u.logCount || 0}</span>
                <Tooltip text={u.bestStreak ? `Best: ${u.bestStreak}d` : "No streak"}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: u.currentStreak > 0 ? "#2e7d32" : "#6D5B80" }}>
                    {u.currentStreak > 0 ? `🔥${u.currentStreak}d` : "—"}
                  </span>
                </Tooltip>
                <span>{u.subscriptionActive ? <span style={{ background: "#f3e5f5", color: "#7e3fc3", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 700 }}>PRO</span> : <span style={{ fontSize: 11, color: "#6D5B80" }}>Free</span>}</span>
                <div onClick={e => e.stopPropagation()} style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "nowrap" }}>
                  {u.subscriptionActive ? (
                    <Tooltip text="Remove premium access from this user">
                      <button style={{ ...s.actBtn, background: "#ffebee", color: "#c62828", fontSize: 11 }} onClick={() => togglePremium(u)} disabled={premiumLoading === u.id}>
                        <MdOutlineBlock size={14} /> {premiumLoading === u.id ? "..." : "Revoke Pro"}
                      </button>
                    </Tooltip>
                  ) : (
                    <Tooltip text="Grant free premium access until end of 2027">
                      <button style={{ ...s.actBtn, background: "#f3e5f5", color: "#7e3fc3", fontSize: 11 }} onClick={() => togglePremium(u)} disabled={premiumLoading === u.id}>
                        <MdOutlineStar size={14} /> {premiumLoading === u.id ? "..." : "Grant Pro"}
                      </button>
                    </Tooltip>
                  )}
                  <Tooltip text="Permanently delete user and Cognito account">
                    <button style={{ ...s.actBtn, background: "#ffebee", color: "#c62828", fontSize: 11 }} onClick={() => del(u.id, u.displayName)}>
                      <MdOutlineDelete size={14} /> Delete
                    </button>
                  </Tooltip>
                  {premiumMsg?.id === u.id && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: premiumMsg.type === "success" ? "#2e7d32" : "#c62828" }}>
                      {premiumMsg.text}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedUserId && (
        <UserDetailModal userId={selectedUserId} hdr={hdr} onClose={() => setSelectedUserId(null)} />
      )}
    </>
  );
}

/* ═══════ CONTENT PAGE ═══════ */
function ContentPage({ hdr }) {
  const [data, setData] = useState({ posts: [], total: 0, page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const fetch_ = useCallback(async (pg = 1) => {
    setLoading(true);
    try { const r = await fetch(`${API}/admin/posts?page=${pg}`, { headers: hdr }); if (r.ok) setData(await r.json()); }
    catch {} finally { setLoading(false); }
  }, [hdr]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const del = async (postId) => {
    if (!window.confirm("Delete this post permanently?")) return;
    await fetch(`${API}/admin/post/${postId}`, { method: "DELETE", headers: hdr });
    fetch_(data.page);
  };

  return (
    <>
      <div style={{ ...s.card, marginBottom: 20, display: "flex", alignItems: "center", gap: 12, padding: "16px 24px" }}>
        <MdOutlineArticle size={22} color="#7e3fc3" />
        <span style={{ fontSize: 15, fontWeight: 700, color: "#40284a" }}>{data.total} total posts</span>
        <span style={{ fontSize: 13, color: "#6D5B80" }}>Page {data.page} of {data.totalPages}</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button style={s.pageBtn} disabled={data.page <= 1} onClick={() => fetch_(data.page - 1)}>Prev</button>
          <button style={s.pageBtn} disabled={data.page >= data.totalPages} onClick={() => fetch_(data.page + 1)}>Next</button>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {loading ? <div style={s.empty}><p>Loading...</p></div> : data.posts.map(post => (
          <div key={post.postId} style={s.card}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <strong style={{ fontSize: 14, color: "#40284a" }}>{post.username}</strong>
              <span style={{ fontSize: 12, color: "#6D5B80" }}>{post.userEmail}</span>
              <span style={{ fontSize: 12, color: "#6D5B80", marginLeft: "auto" }}>{timeAgo(post.time)}</span>
            </div>
            <p style={{ fontSize: 14, color: "#33283a", margin: "0 0 8px", lineHeight: 1.5 }}>{post.content}</p>
            {post.photoUrl && (
              <div style={{ marginBottom: 8 }}>
                <MdOutlineImage size={16} color="#8b7a9e" style={{ verticalAlign: "middle", marginRight: 4 }} />
                <a href={post.photoUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#7e3fc3" }}>View photo</a>
              </div>
            )}
            <div style={{ borderTop: "1px solid #e8c8d8", paddingTop: 8, display: "flex", gap: 8 }}>
              <Tooltip text="Permanently delete this post">
                <button style={{ ...s.actBtn, background: "#ffebee", color: "#c62828" }} onClick={() => del(post.postId)}>
                  <MdOutlineDelete size={14} /> Delete
                </button>
              </Tooltip>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ═══════ NOTIFICATIONS PAGE ═══════ */
function NotificationsPage({ hdr }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const send = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) return;
    if (!window.confirm(`Send "${title}" to ALL users?`)) return;
    setSending(true); setResult(null);
    try {
      const r = await fetch(`${API}/admin/notifications/broadcast`, {
        method: "POST", headers: { ...hdr, "Content-Type": "application/json" },
        body: JSON.stringify({ title, message }),
      });
      const d = await r.json(); setResult(d); setTitle(""); setMessage("");
    } catch { setResult({ error: true }); } finally { setSending(false); }
  };

  return (
    <>
      <div className="admin-grid-3" style={s.grid3}>
        <StatCard label="Broadcast" value="Push" icon={<MdOutlineNotifications size={24} color="#7e3fc3" />} color="#7e3fc3" />
        <StatCard label="Delivery" value="Expo" icon={<MdOutlineDevices size={24} color="#1565c0" />} color="#1565c0" />
        <StatCard label="Instant" value="Live" icon={<MdOutlineSend size={24} color="#2e7d32" />} color="#2e7d32" />
      </div>
      <div style={{ ...s.card, maxWidth: 600 }}>
        <SectionTitle icon={<MdOutlineSend size={20} color="#7e3fc3" />} title="Send Broadcast Notification" subtitle="Push to all active devices" />
        <p style={{ fontSize: 13, color: "#6D5B80", margin: "0 0 20px", lineHeight: 1.5 }}>
          This will send a push notification to <strong>all users</strong> with active devices. Use for announcements, updates, or important messages.
        </p>
        <form onSubmit={send} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={s.label}>Title</label>
            <input type="text" placeholder="e.g. New Feature Available!" value={title} onChange={e => setTitle(e.target.value)} style={{ ...s.input, textAlign: "left", width: "100%" }} />
          </div>
          <div>
            <label style={s.label}>Message</label>
            <textarea placeholder="Write your notification message..." value={message} onChange={e => setMessage(e.target.value)} rows={4} style={{ ...s.input, textAlign: "left", width: "100%", resize: "vertical", fontFamily: "inherit" }} />
          </div>
          <button type="submit" style={{ ...s.primaryBtn, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, maxWidth: 200 }} disabled={sending || !title.trim() || !message.trim()}>
            <MdOutlineSend size={18} /> {sending ? "Sending..." : "Send to All"}
          </button>
        </form>
        {result && !result.error && (
          <div style={{ marginTop: 20, padding: 16, borderRadius: 12, background: "#e8f5e9", color: "#2e7d32", fontSize: 14 }}>
            Sent to {result.sent} devices ({result.failed} failed)
          </div>
        )}
        {result?.error && (
          <div style={{ marginTop: 20, padding: 16, borderRadius: 12, background: "#ffebee", color: "#c62828", fontSize: 14 }}>
            Failed to send. Check server logs.
          </div>
        )}
      </div>
    </>
  );
}

/* ═══════ ANALYTICS PAGE ═══════ */
function AnalyticsPage({ hdr }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const r = await fetch(`${API}/admin/analytics`, { headers: hdr }); if (r.ok) setData(await r.json()); }
      catch {} finally { setLoading(false); }
    })();
  }, [hdr]);

  if (loading) return <div style={s.empty}><p>Loading analytics...</p></div>;
  if (!data) return <div style={s.empty}><p>Could not load analytics</p></div>;

  return (
    <>
      <div className="admin-grid-4" style={s.grid4}>
        <StatCard label="Total Users" value={data.totalUsers} icon={<MdOutlineGroup size={24} color="#7e3fc3" />} color="#7e3fc3" />
        <StatCard label="Active (7d)" value={data.activeUsers7d} icon={<MdOutlineSpeed size={24} color="#2e7d32" />} color="#2e7d32" />
        <StatCard label="Active (30d)" value={data.activeUsers30d} icon={<MdOutlineCalendarToday size={24} color="#1565c0" />} color="#1565c0" />
        <StatCard label="Total Logs" value={data.totalLogs} icon={<MdOutlineBarChart size={24} color="#e65100" />} color="#e65100" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        <div style={s.card}>
          <SectionTitle icon={<MdOutlineBarChart size={20} color="#7e3fc3" />} title="Daily Health Logs" subtitle="Last 30 days" />
          {data.dailyLogs?.length > 0 ? <MiniBar data={data.dailyLogs} color="#7e3fc3" height={100} /> : <p style={{ color: "#6D5B80", fontSize: 13 }}>No data yet</p>}
        </div>
        <div style={s.card}>
          <SectionTitle icon={<MdOutlineForum size={20} color="#1565c0" />} title="Daily Posts" subtitle="Last 30 days" />
          {data.dailyPosts?.length > 0 ? <MiniBar data={data.dailyPosts} color="#1565c0" height={100} /> : <p style={{ color: "#6D5B80", fontSize: 13 }}>No data yet</p>}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        <div style={s.card}>
          <SectionTitle icon={<MdOutlineTrendingUp size={20} color="#2e7d32" />} title="Community Activity" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ padding: 16, background: "#fce6ee", borderRadius: 14, textAlign: "center" }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#40284a" }}>{data.postsLast7d}</div>
              <div style={{ fontSize: 12, color: "#6D5B80" }}>Posts (7d)</div>
            </div>
            <div style={{ padding: 16, background: "#fce6ee", borderRadius: 14, textAlign: "center" }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#40284a" }}>{data.postsLast30d}</div>
              <div style={{ fontSize: 12, color: "#6D5B80" }}>Posts (30d)</div>
            </div>
          </div>
        </div>
        <div style={s.card}>
          <SectionTitle icon={<MdOutlineStar size={20} color="#e65100" />} title="Top Active Users" subtitle="Last 30 days" />
          {data.topUsers?.length > 0 ? data.topUsers.map((u, i) => (
            <div key={u.userId} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "8px 0",
              borderBottom: i < data.topUsers.length - 1 ? "1px solid #e8c8d8" : "none",
            }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: ["#7e3fc3", "#1565c0", "#2e7d32", "#e65100", "#c62828"][i % 5],
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontSize: 12, fontWeight: 800,
              }}>{i + 1}</div>
              <span style={{ fontSize: 14, color: "#40284a", flex: 1, fontWeight: 600 }}>{u.displayName}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#7e3fc3" }}>{u.logCount} logs</span>
            </div>
          )) : <p style={{ color: "#6D5B80", fontSize: 13 }}>No activity yet</p>}
        </div>
      </div>
    </>
  );
}

/* ═══════ SUBSCRIPTIONS PAGE ═══════ */
function SubscriptionsPage({ hdr, stats }) {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/admin/subscribers`, { headers: hdr });
        if (r.ok) setSubscribers(await r.json());
      } catch {} finally { setLoading(false); }
    })();
  }, [hdr]);

  const paidSubscribers = subscribers.filter(s => s.subscriptionPlan && s.subscriptionPlan !== 'admin_grant');
  const totalMRR = paidSubscribers.reduce((sum, s) => sum + (s.subscriptionPlan === 'yearly' ? 5.00 : 5.99), 0);

  return (
    <>
      <div className="admin-grid-3" style={s.grid3}>
        <StatCard label="MRR" value={`€${totalMRR.toFixed(2)}`} icon={<MdOutlineTrendingUp size={24} color="#2e7d32" />} color="#2e7d32" />
        <StatCard label="Active Subscribers" value={`${paidSubscribers.length} paid + ${subscribers.length - paidSubscribers.length} granted`} icon={<MdOutlineStar size={24} color="#7e3fc3" />} color="#7e3fc3" />
        <StatCard label="Free Users" value={(stats?.totalUsers || 0) - subscribers.length} icon={<MdOutlinePerson size={24} color="#1565c0" />} color="#1565c0" />
      </div>

      <div style={{ marginBottom: 16 }}>
        <a href="https://dashboard.stripe.com/billing/overview" target="_blank" rel="noreferrer"
          style={{ ...s.actBtn, background: "#635bff", color: "#fff", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 20px" }}>
          Open Stripe Dashboard →
        </a>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {loading ? <div style={s.empty}><p>Loading...</p></div> :
        subscribers.length === 0 ? (
          <div style={s.empty}>
            <MdOutlineStar size={48} color="#c4b5d0" />
            <h3 style={{ color: "#6d5b80", marginTop: 12 }}>No subscribers yet</h3>
          </div>
        ) : (
          <div className="admin-table-wrap">
            <div className="admin-table-row" style={{
              display: "grid", gridTemplateColumns: "50px 1.5fr 1.5fr 100px 100px",
              gap: 12, padding: "10px 24px", background: "#fce6ee", fontWeight: 700, fontSize: 12,
              color: "#6d5b80", borderRadius: 12,
            }}>
              <span>ID</span><span>Name</span><span>Email</span><span>Plan</span><span>Condition</span>
            </div>
            {subscribers.filter(u => u.subscriptionPlan !== 'admin_grant').map(u => (
              <div key={u.id} className="admin-table-row" style={{
                ...s.card, display: "grid", gridTemplateColumns: "50px 1.5fr 1.5fr 100px 100px",
                gap: 12, alignItems: "center", padding: "14px 24px", borderRadius: 14, marginBottom: 8,
              }}>
                <span style={{ fontSize: 12, color: "#6D5B80" }}>#{u.id}</span>
                <strong style={{ fontSize: 14, color: "#40284a" }}>{u.displayName || "—"}</strong>
                <span style={{ fontSize: 12, color: "#6d5b80" }}>{u.email}</span>
                <span style={{ background: "#f3e5f5", color: "#7e3fc3", padding: "2px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700, textAlign: "center" }}>
                  {u.subscriptionPlan || "active"}
                </span>
                <span style={{ fontSize: 11, color: "#40284a" }}>{u.condition || "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/* ═══════ FOUNDER INTELLIGENCE PAGE ═══════ */
function FounderPage({ hdr, stats }) {
  const [conditions, setConditions] = useState(null);
  const [geography, setGeography] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [retention, setRetention] = useState(null);
  const [medications, setMedications] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const endpoints = [
        { url: 'founder/conditions', setter: setConditions },
        { url: 'founder/geography', setter: setGeography },
        { url: 'founder/funnel', setter: setFunnel },
        { url: 'founder/retention', setter: setRetention },
        { url: 'founder/medications', setter: setMedications },
      ];
      await Promise.allSettled(endpoints.map(async ({ url, setter }) => {
        try {
          const r = await fetch(`${API}/admin/${url}`, { headers: hdr });
          if (r.ok) setter(await r.json());
        } catch {}
      }));
      setLoading(false);
    })();
  }, [hdr]);

  if (loading) return <div style={s.empty}><p>Loading intelligence data...</p></div>;

  const funnelSteps = funnel ? [
    { label: "Signed up", value: funnel.totalUsers, color: "#7e3fc3" },
    { label: "Logged once", value: funnel.loggedOnce, color: "#1565c0" },
    { label: "7+ days", value: funnel.logged7Days, color: "#2e7d32" },
    { label: "14+ days", value: funnel.logged14Days, color: "#e65100" },
    { label: "Premium", value: funnel.premium, color: "#c62828" },
  ] : [];

  const maxFunnel = Math.max(1, funnel?.totalUsers || 1);

  return (
    <>
      {/* Conversion Funnel */}
      <div style={s.card}>
        <SectionTitle icon={<MdOutlineTrendingUp size={20} color="#7e3fc3" />} title="Conversion Funnel" subtitle="From signup to premium" />
        <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 140, marginBottom: 12 }}>
          {funnelSteps.map((step, i) => (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: step.color }}>{step.value}</div>
              <div style={{
                width: "100%", background: `linear-gradient(180deg, ${step.color}, ${step.color}66)`,
                borderRadius: 8, marginTop: 4,
                height: Math.max(8, (step.value / maxFunnel) * 100),
                transition: "height 0.5s",
              }} />
              <div style={{ fontSize: 11, color: "#6D5B80", marginTop: 6, textAlign: "center", fontWeight: 600 }}>{step.label}</div>
              {i > 0 && funnel?.totalUsers > 0 && (
                <div style={{ fontSize: 10, color: step.color, fontWeight: 700, marginTop: 2 }}>
                  {Math.round((step.value / funnel.totalUsers) * 100)}%
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        {/* Condition Breakdown */}
        <div style={s.card}>
          <SectionTitle icon={<MdOutlinePerson size={20} color="#7e3fc3" />} title="Users by Condition" />
          {conditions && conditions.length > 0 ? conditions.map((c, i) => {
            const pct = stats?.totalUsers > 0 ? Math.round((c.count / stats.totalUsers) * 100) : 0;
            const colors = ["#7e3fc3", "#1565c0", "#2e7d32", "#e65100", "#c62828", "#6a1b9a", "#00838f", "#ad1457", "#4527a0", "#1b5e20"];
            return (
              <div key={c.condition} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: i < conditions.length - 1 ? "1px solid #e8c8d8" : "none" }}>
                <div style={{ width: 10, height: 10, borderRadius: 5, background: colors[i % 10], flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 14, color: "#40284a", fontWeight: 600, textTransform: "capitalize" }}>{c.condition}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#33283a" }}>{c.count}</span>
                <span style={{ fontSize: 12, color: "#6D5B80", minWidth: 36, textAlign: "right" }}>{pct}%</span>
              </div>
            );
          }) : <p style={{ color: "#6D5B80", fontSize: 13 }}>No condition data yet</p>}
        </div>

        {/* Geographic Breakdown */}
        <div style={s.card}>
          <SectionTitle icon={<MdOutlineCalendarToday size={20} color="#1565c0" />} title="Users by Country" />
          {geography && geography.length > 0 ? geography.map((g, i) => (
            <div key={g.country} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: i < geography.length - 1 ? "1px solid #e8c8d8" : "none" }}>
              <span style={{ flex: 1, fontSize: 14, color: "#40284a", fontWeight: 600 }}>{g.country}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#33283a" }}>{g.count}</span>
            </div>
          )) : <p style={{ color: "#6D5B80", fontSize: 13 }}>No geographic data yet</p>}
        </div>
      </div>

      {/* Retention Cohorts */}
      <div style={s.card}>
        <SectionTitle icon={<MdOutlineBarChart size={20} color="#2e7d32" />} title="Retention Cohorts" subtitle="Users returning by signup week" />
        {retention && retention.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0 6px", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#fce6ee" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", color: "#6d5b80", fontWeight: 700 }}>Signup Week</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", color: "#6d5b80", fontWeight: 700 }}>Signups</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", color: "#6d5b80", fontWeight: 700 }}>Week 1</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", color: "#6d5b80", fontWeight: 700 }}>Week 2</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", color: "#6d5b80", fontWeight: 700 }}>Week 3</th>
                  <th style={{ padding: "12px 16px", textAlign: "center", color: "#6d5b80", fontWeight: 700 }}>Week 4</th>
                </tr>
              </thead>
              <tbody>
                {retention.map((cohort, i) => (
                  <tr key={i} style={{ background: "#fce6ee", borderRadius: 10 }}>
                    <td style={{ padding: "12px 16px", fontWeight: 600, color: "#40284a" }}>{cohort.weekLabel}</td>
                    <td style={{ padding: "12px 16px", textAlign: "center", fontWeight: 700, color: "#7e3fc3" }}>{cohort.signups}</td>
                    {[0, 1, 2, 3].map(w => {
                      const r = cohort.retention[w];
                      if (!r) return <td key={w} style={{ padding: "12px 16px", textAlign: "center", color: "#ccc" }}>—</td>;
                      const bg = r.pct >= 50 ? "#e8f5e9" : r.pct >= 25 ? "#fff8e1" : "#ffebee";
                      const color = r.pct >= 50 ? "#2e7d32" : r.pct >= 25 ? "#e65100" : "#c62828";
                      return (
                        <td key={w} style={{ padding: "12px 16px", textAlign: "center" }}>
                          <span style={{ background: bg, color, padding: "3px 10px", borderRadius: 10, fontWeight: 700, fontSize: 12 }}>
                            {r.pct}% ({r.active})
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p style={{ color: "#6D5B80", fontSize: 13 }}>Not enough data for cohort analysis yet</p>}
      </div>

      {/* Top Medications */}
      <div style={{ ...s.card, marginTop: 20 }}>
        <SectionTitle icon={<MdOutlineStar size={20} color="#e65100" />} title="Most Tracked Medications" subtitle="Across all users — valuable for data partnerships" />
        {medications && medications.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {medications.map((m, i) => (
              <div key={m.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#fce6ee", borderRadius: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: "#7e3fc3", minWidth: 20 }}>#{i + 1}</span>
                <span style={{ flex: 1, fontSize: 13, color: "#40284a", fontWeight: 600, textTransform: "capitalize" }}>{m.name}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#33283a" }}>{m.count}x</span>
              </div>
            ))}
          </div>
        ) : <p style={{ color: "#6D5B80", fontSize: 13 }}>No medication data yet</p>}
      </div>
    </>
  );
}

/* ═══════ MAIN ═══════ */
export default function AdminDashboard() {
  const [adminKey, setAdminKey] = useState(localStorage.getItem("ninoa_admin_key") || "");
  const [auth, setAuth] = useState(false);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const hdr = { "x-admin-key": adminKey };

  useEffect(() => {
    const closeOnDesktop = () => {
      if (window.innerWidth > 900 && sidebarOpen) setSidebarOpen(false);
    };
    window.addEventListener("resize", closeOnDesktop);
    return () => window.removeEventListener("resize", closeOnDesktop);
  }, [sidebarOpen]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/stats`, { headers: { "x-admin-key": adminKey } });
      if (!r.ok) { setAuth(false); localStorage.removeItem("ninoa_admin_key"); return; }
      setStats(await r.json()); setAuth(true);
    } catch {} finally { setLoading(false); }
  }, [adminKey]);

  useEffect(() => { if (adminKey) fetchStats(); else setLoading(false); }, [adminKey, fetchStats]);

  if (loading) return (
    <div style={s.loginWrapper}>
      <div style={{ textAlign: "center" }}>
        <img src={LOGO} alt="Ninoa" style={{ width: 56, height: 56, borderRadius: 16, marginBottom: 16, animation: "pulse 1.5s ease-in-out infinite" }} />
        <p style={{ color: "#6D5B80", fontSize: 14 }}>Loading...</p>
      </div>
    </div>
  );
  if (!auth || !adminKey) return <LoginScreen onLogin={k => { setAdminKey(k); setAuth(true); }} />;

  const nav = [
    { key: "overview", icon: <MdOutlineDashboard size={20} />, label: "Overview" },
    { key: "reports", icon: <MdOutlineFlag size={20} />, label: "Reports", badge: stats?.pendingReports },
    { key: "users", icon: <MdOutlineGroup size={20} />, label: "Users" },
    { key: "content", icon: <MdOutlineArticle size={20} />, label: "Content" },
    { key: "notifications", icon: <MdOutlineNotifications size={20} />, label: "Notifications" },
    { key: "analytics", icon: <MdOutlineBarChart size={20} />, label: "Analytics" },
    { key: "subscriptions", icon: <MdOutlinePayments size={20} />, label: "Subscriptions" },
    { key: "founder", icon: <MdOutlineTrendingUp size={20} />, label: "Founder Intel" },
  ];
  const titles = {
    overview: "Overview",
    reports: "Content Moderation",
    users: "User Management",
    content: "Content Feed",
    notifications: "Push Notifications",
    analytics: "Analytics",
    subscriptions: "Subscriptions",
    founder: "Founder Intelligence",
  };

  return (
    <div style={s.dashboard} className="admin-dashboard">
      {/* Mobile backdrop — visible only when drawer is open on mobile */}
      <div
        className={`admin-backdrop ${sidebarOpen ? "admin-backdrop-open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar (becomes a slide-in drawer on mobile) */}
      <aside
        style={s.sidebar}
        className={`admin-sidebar ${sidebarOpen ? "admin-sidebar-open" : ""}`}
      >
        <div style={s.sidebarLogo}>
          <img src={LOGO} alt="Ninoa" style={{ width: 36, height: 36, borderRadius: 10 }} />
          <span style={{ fontSize: 16, fontWeight: 700, color: "#3D2B42", marginLeft: 12 }}>Ninoa</span>
        </div>
        <nav style={{ padding: "20px 12px", flex: 1 }}>
          {nav.map(item => (
            <div key={item.key} onClick={() => { setPage(item.key); setSidebarOpen(false); }}
              style={{ ...s.navItem, ...(page === item.key ? s.navActive : {}) }}>
              {item.icon}
              <span>{item.label}</span>
              {item.badge > 0 && <span style={s.badge}>{item.badge}</span>}
            </div>
          ))}
        </nav>
        <div style={{ padding: "0 16px 16px" }}>
          <div style={{ padding: "12px 16px", background: "rgba(255,255,255,0.06)", borderRadius: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", fontWeight: 600, marginBottom: 4 }}>NINOA ADMIN</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>v1.0 &middot; Production</div>
          </div>
          <button onClick={() => { localStorage.removeItem("ninoa_admin_key"); setAdminKey(""); setAuth(false); }}
            style={{ ...s.logoutBtn, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%" }}>
            <MdOutlineLogout size={16} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main style={s.main} className="admin-main">
        <header className="admin-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
              className="admin-hamburger"
              style={{
                background: "#fff",
                border: "1.5px solid #e8d5e0",
                borderRadius: 12,
                width: 42,
                height: 42,
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#3D2B42",
                flexShrink: 0,
              }}
            >
              <MdOutlineMenu size={22} />
            </button>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ fontSize: 26, fontWeight: 800, color: "#40284a", margin: 0 }}>{titles[page]}</h1>
              <p style={{ fontSize: 13, color: "#6D5B80", margin: "4px 0 0" }}>
                {page === "overview" ? "Your product at a glance" :
                 page === "reports" ? "Review and moderate user reports" :
                 page === "users" ? "Manage your user base" :
                 page === "content" ? "Browse and moderate all posts" :
                 page === "notifications" ? "Send push notifications to users" :
                 page === "analytics" ? "Deep dive into usage data" :
                 page === "founder" ? "Conditions, funnels, retention, medications — your data goldmine" :
                 "Manage subscription plans and revenue"}
              </p>
            </div>
          </div>
          <button onClick={fetchStats} style={{ ...s.refreshBtn, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <MdOutlineRefresh size={18} /> <span className="admin-refresh-label">Refresh</span>
          </button>
        </header>

        {page === "overview" && <OverviewPage hdr={hdr} stats={stats} />}
        {page === "reports" && <ReportsPage hdr={hdr} stats={stats} onRefresh={fetchStats} />}
        {page === "users" && <UsersPage hdr={hdr} stats={stats} />}
        {page === "content" && <ContentPage hdr={hdr} />}
        {page === "notifications" && <NotificationsPage hdr={hdr} />}
        {page === "analytics" && <AnalyticsPage hdr={hdr} />}
        {page === "subscriptions" && <SubscriptionsPage hdr={hdr} stats={stats} />}
        {page === "founder" && <FounderPage hdr={hdr} stats={stats} />}
      </main>

      {/* CSS animations + responsive */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

        /* Hamburger + backdrop hidden by default (desktop) */
        .admin-hamburger { display: none !important; }
        .admin-backdrop { display: none; }

        /* Tablet — 2-column grids */
        @media (max-width: 900px) {
          .admin-grid-4 { grid-template-columns: repeat(2, 1fr) !important; gap: 14px !important; }
          .admin-grid-3 { grid-template-columns: repeat(2, 1fr) !important; gap: 14px !important; }
          .admin-main { padding: 24px 20px !important; }
          .admin-hamburger { display: flex !important; }

          .admin-sidebar {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            height: 100vh !important;
            width: 80vw !important;
            max-width: 280px;
            z-index: 1000;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            box-shadow: 4px 0 24px rgba(0,0,0,0.18);
          }
          .admin-sidebar-open { transform: translateX(0) !important; }

          .admin-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(40, 25, 50, 0.45);
            z-index: 999;
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.25s ease;
          }
          .admin-backdrop-open {
            display: block;
            opacity: 1 !important;
            pointer-events: auto !important;
          }
        }

        /* Phone — 1-column grids, tighter padding */
        @media (max-width: 600px) {
          .admin-grid-4, .admin-grid-3 {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
          .admin-main { padding: 18px 14px !important; }
          .admin-header h1 { font-size: 20px !important; }
          .admin-header p { font-size: 12px !important; }
          .admin-refresh-label { display: none; }
        }

        /* Tables — wide grid columns wrapped in horizontal-scroll container.
           Page itself stays narrow; the table scrolls within its own wrapper. */
        .admin-table-wrap {
          width: 100%;
        }
        @media (max-width: 900px) {
          .admin-main { overflow-x: hidden !important; }
          .admin-table-wrap {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            margin: 0 -14px;
            padding: 0 14px;
          }
          .admin-table-row {
            min-width: 720px;
          }
        }
      `}</style>
    </div>
  );
}

/* ═══════ STYLES ═══════ */
const s = {
  loginWrapper: {
    minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
    background: "linear-gradient(135deg, #fce6ee 0%, #f4d6e4 50%, #fce6ee 100%)",
  },
  loginCard: {
    background: "#fff", borderRadius: 24, padding: "48px 40px", width: 380, maxWidth: "90vw",
    boxShadow: "0 20px 60px rgba(61,43,66,0.12)", textAlign: "center",
  },
  input: {
    padding: "14px 18px", borderRadius: 14, border: "1.5px solid #e8d5e0", fontSize: 15,
    outline: "none", textAlign: "center", boxSizing: "border-box", transition: "border-color 0.2s",
    background: "#fff",
  },
  primaryBtn: {
    padding: "14px", borderRadius: 14, border: "none",
    background: "#3D2B42", color: "#fff",
    fontSize: 15, fontWeight: 700, cursor: "pointer", transition: "opacity 0.2s",
  },
  refreshBtn: {
    padding: "10px 20px", borderRadius: 12, border: "1.5px solid #e8d5e0",
    background: "#fff", color: "#3D2B42", fontSize: 13, fontWeight: 600,
    cursor: "pointer", transition: "all 0.2s",
  },
  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#3D2B42", marginBottom: 6 },
  dashboard: { display: "flex", minHeight: "100vh", background: "#fce6ee" },
  sidebar: {
    width: 250, background: "#fce6ee",
    padding: "24px 0", display: "flex", flexDirection: "column", flexShrink: 0,
    position: "sticky", top: 0, height: "100vh",
  },
  sidebarLogo: {
    display: "flex", alignItems: "center", padding: "0 24px 24px",
    borderBottom: "1px solid #f0d8e4",
  },
  navItem: {
    display: "flex", alignItems: "center", gap: 10, padding: "13px 18px", borderRadius: 14,
    color: "#6D5B80", fontSize: 14, fontWeight: 600, cursor: "pointer",
    marginBottom: 2, transition: "all 0.2s",
  },
  navActive: { background: "#fff5f8", color: "#3D2B42" },
  badge: {
    marginLeft: "auto", background: "#e53935", color: "#fff", fontSize: 11,
    fontWeight: 700, padding: "2px 8px", borderRadius: 10,
  },
  logoutBtn: {
    padding: "10px", borderRadius: 12, border: "1px solid #e8d5e0",
    background: "transparent", color: "#6D5B80", fontSize: 13, cursor: "pointer",
  },
  main: { flex: 1, padding: "32px 40px", overflow: "auto" },
  card: {
    background: "#fff5f8", borderRadius: 20, padding: "20px 24px",
    boxShadow: "0 2px 12px rgba(61,43,66,0.06)",
  },
  grid4: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 20, marginBottom: 28 },
  grid3: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginBottom: 28 },
  filterRow: { display: "flex", gap: 8, marginBottom: 20 },
  filterBtn: {
    padding: "8px 18px", borderRadius: 20, border: "1.5px solid #e8d5e0",
    background: "#fff", color: "#6D5B80", fontSize: 13, fontWeight: 600, cursor: "pointer",
    transition: "all 0.2s",
  },
  filterActive: { background: "#7e3fc3", color: "#fff", borderColor: "#7e3fc3" },
  actBtn: {
    padding: "8px 16px", borderRadius: 14, border: "none", fontSize: 12,
    fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
    transition: "opacity 0.2s",
  },
  pageBtn: {
    padding: "8px 16px", borderRadius: 14, border: "1.5px solid #e8d5e0",
    background: "#fff", color: "#3D2B42", fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  empty: {
    textAlign: "center", padding: "60px 20px", background: "#fff", borderRadius: 20,
    boxShadow: "0 2px 12px rgba(61,43,66,0.06)",
  },
  tooltip: {
    position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)",
    background: "#2B192E", color: "#fff", padding: "8px 14px", borderRadius: 10,
    fontSize: 12, lineHeight: 1.4, width: 220, textAlign: "center", zIndex: 100,
    boxShadow: "0 4px 16px rgba(0,0,0,0.15)", pointerEvents: "none",
  },
};
